import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

function useClientBookingData(therapistId) {
  const [schedules, setSchedules] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [services, setServices] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // id-parametern används när therapistId inte ännu finns i hook-scopet,
  // t.ex. vid initial load i fetchClientData innan client-state är satt.
  const refetch = useCallback(async (id) => {
    const tid = id || therapistId;
    if (!tid) return;
    setLoading(true);
    setError(null);

    try {
      const { data: schedData, error: schedError } = await supabase
        .from('availability_schedules')
        .select('*')
        .eq('therapist_id', tid)
        .eq('is_active', true)
        .order('day_of_week');
      if (schedError) throw schedError;
      setSchedules(schedData || []);

      const { data: bookData, error: bookError } = await supabase
        .from('bookings')
        .select('*, service:services(*)')
        .eq('therapist_id', tid)
        .in('status', ['confirmed', 'cancelled'])
        .order('date')
        .order('start_time');
      if (bookError) throw bookError;
      setBookings((bookData || []).filter(b => b.status === 'confirmed'));
      setCancelledBookings((bookData || []).filter(b => b.status === 'cancelled'));

      // T-16: blockerade tider via RPC utan anledning (reason) – kunden ska aldrig se den.
      const blockFrom = new Date().toISOString().slice(0, 10);
      const blockTo = new Date(Date.now() + 366 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const { data: blockedData, error: blockedError } = await supabase
        .rpc('get_blocked_slots', { p_therapist_id: tid, p_from_date: blockFrom, p_to_date: blockTo });
      if (blockedError) throw blockedError;
      setBlockedTimes(blockedData || []);

      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('*')
        .eq('therapist_id', tid)
        .eq('is_active', true)
        .eq('is_deleted', false)
        .order('duration');
      if (servicesError) throw servicesError;
      setServices(servicesData || []);

      const today = new Date();
      const fromDate = today.toISOString().slice(0, 10);
      const toDate = new Date(today.getTime() + 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const { data: slotsData, error: slotsError } = await supabase
        .rpc('get_booked_slots', { p_therapist_id: tid, p_from_date: fromDate, p_to_date: toDate });
      if (slotsError) throw slotsError;
      setBookedSlots(slotsData || []);
    } catch (err) {
      setError(err.message || 'Något gick fel vid hämtning av bokningsdata.');
    }

    setLoading(false);
  }, [therapistId]);

  return { schedules, bookings, cancelledBookings, blockedTimes, services, bookedSlots, loading, error, refetch };
}

export default useClientBookingData;
